import { useRouter } from 'next/router';
import { useAchievementTypes, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import {
  AchievementUserPreviousCompletions,
  AchievementCompletionsFillTemplateConversation,
  AchievementCompletionsFillTemplateTasklist,
  AchievementCompletionsSetConfidential,
  AchievementGroupsOfUsersGroupFillTemplate,
  AchievementMultipleCompletionsList,
  AchievementCompletionItemsCleared
} from 'components/achievements';
import { EmptyIndicator, FlexContainer } from 'components/common';
import { Box } from 'components/common/box';
import {
  ProfileAchievementsAchievementInfo,
  ProfileAchievementsAchievementProgress
} from '.';

import type { GetUserAchievementsRes } from 'shared-types/responses';

interface Props {
  achievementId: number;
  isUserPage?: boolean;
}

const ProfileAchievementsAchievement = ({
  achievementId,
  isUserPage
}: Props) => {
  const {
    messages,
    pageState: { user },
    theme,
    user: loggedInUser
  } = useReduxState();

  const achievementTypes = useAchievementTypes();

  const router = useRouter();
  const groupCompletionId = router.query.groupCompletionId as string;

  const { data, loading, updateData } = useApiData<GetUserAchievementsRes>({
    url:
      `/admin/users/${user.id}/achievements/${achievementId}` +
      (groupCompletionId ? `?groupCompletionId=${groupCompletionId}` : ''),
    onlyIf: Boolean(user)
  });

  const handleBack = () => {
    const queryParams = { ...router.query };
    delete queryParams.groupCompletionId;

    router.replace({ query: queryParams }, undefined, { shallow: true });
  };

  const achievement = data?.achievements?.[0];
  const completion = achievement?.completions?.[0];

  const templateComponents = {
    conversation: AchievementCompletionsFillTemplateConversation,
    'group-conversation': AchievementGroupsOfUsersGroupFillTemplate,
    tasklist: AchievementCompletionsFillTemplateTasklist
  };

  const TemplateElement = templateComponents[achievement?.type] as
    | typeof AchievementCompletionsFillTemplateConversation
    | typeof AchievementCompletionsFillTemplateTasklist
    | typeof AchievementGroupsOfUsersGroupFillTemplate;

  return (
    <div className="summary">
      <div className="side">
        <ProfileAchievementsAchievementInfo
          achievement={achievement}
          isUserPage={isUserPage}
          loading={loading}
          targetUser={user}
        />
      </div>

      <div className="main">
        {achievement?.completions?.length > 1 ? (
          <AchievementMultipleCompletionsList
            achievement={achievement}
            completions={achievement.completions}
          />
        ) : (
          <>
            <ProfileAchievementsAchievementProgress
              achievement={achievement}
              completion={completion}
              loading={loading}
              {...(groupCompletionId ? { onBack: handleBack } : {})}
            />

            {completion?.previousCompletions?.length ? (
              <div className="previous-completions">
                <AchievementUserPreviousCompletions
                  achievement={achievement}
                  previousCompletions={completion.previousCompletions}
                  userId={user?.id}
                />
              </div>
            ) : null}

            {['conversation', 'group-conversation', 'tasklist'].includes(
              achievement?.type
            ) &&
            (isUserPage ||
              completion?.status === 'completed' ||
              achievement?.isIncompleteViewableByUser) ? (
              <Box
                padding={0}
                title={
                  <FlexContainer
                    style={{
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 10,
                      width: '100%'
                    }}
                  >
                    <div style={{ display: 'flex', gap: 7 }}>
                      <div>
                        {achievementTypes[achievement?.type].templateHeader}
                      </div>
                      <div
                        style={{
                          color: theme.textColorLighter,
                          fontSize: '1.5rem',
                          fontWeight: 600
                        }}
                      >
                        {completion.canSeeCompletionItems
                          ? `(${completion.numberOfCompletedTemplateItems}/${achievement.numberOfItems})`
                          : ''}
                      </div>
                    </div>

                    <AchievementCompletionsSetConfidential
                      achievementId={achievement?.id}
                      achievementCompletionId={completion?.id}
                      disabled={true}
                      isConfidential={completion?.isConfidential}
                      isUserOrMember={loggedInUser?.id === user?.id}
                      users={[user]}
                      updateAchievement={() => null}
                      {...(isUserPage ? { targetUserId: user?.id } : {})}
                    />
                  </FlexContainer>
                }
              >
                {completion.completionItemsClearedDate ? (
                  <AchievementCompletionItemsCleared
                    date={completion.completionItemsClearedDate}
                    type={achievement.type}
                  />
                ) : completion.canSeeCompletionItems ? (
                  completion.items?.map((item, i) => {
                    return (
                      // the profile achievement page is accessible either by the user
                      // or by an admin/supervisor looking at a user's page
                      // so we disable edit entirely and make this a read-only state
                      <TemplateElement
                        key={item.id}
                        achievement={achievement}
                        adminMode={isUserPage}
                        canAnswer={false}
                        canMarkComplete={false}
                        completionId={completion?.id}
                        item={item}
                        itemIndex={i + 1}
                        targetUserId={user.id}
                        updateItems={updateData}
                      />
                    );
                  })
                ) : (
                  <EmptyIndicator
                    description={messages.achievementCompletionConfidentialInfo}
                    icon="lock"
                    title={messages.commonConfidential}
                  />
                )}
              </Box>
            ) : null}
          </>
        )}
      </div>

      <style jsx>{`
        .summary {
          display: flex;
          flex-direction: column;
          gap: 30px 25px;
        }
        .main {
          display: flex;
          flex-direction: column;
          gap: 30px;
        }
        @media (min-width: ${breakpoints.large}px) {
          .summary {
            flex-direction: row;
          }
          .side {
            flex-shrink: 0;
            width: 360px;
          }
          .main {
            flex-grow: 1;
            min-width: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default ProfileAchievementsAchievement;
